import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, CheckCircle2, FolderOpen, Loader2, Users, X } from 'lucide-react'
import api from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import { useBranding } from '../../hooks/useBranding.jsx'
import { formatDateTime } from '../../utils/helpers'
import StatCard from '../platform/StatCard.jsx'
import ViewChart from '../platform/ViewChart.jsx'
import { themeVars } from '../platform/dashboardTheme.js'
import DecideButtons from '../approvals/DecideButtons.jsx'
import useFreshOnReturn from '../../hooks/useFresh.js'
import { companyTheme } from './companyTheme.js'

/**
 * A company's front page -- the first thing their people see after signing in at their own address.
 *
 * Built from the partner dashboard's own parts, as the plan promised ("same parts, their numbers"): the
 * same banner, the same number cards and the same chart, in the company's colour (companyTheme.js), which
 * is also the colour of the selected item in their menu. Their numbers come from GET /approvals/front:
 * what waits for them, what was sent back to them, what waits for the team to agree, which dossiers are
 * ready to submit; the documents filed week by week; and the approvals themselves, decided right here.
 */

const OUR_LOGO = '/3pillars-logo.png'
const initials = (name) => (name || 'ProTrack').split(/\s+/).filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase()
// Month first ("Nov 2"): the chart reads that as time, and says how the weeks went rather than ranking them.
const shortDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

function Banner({ theme, name, logo, role, who }) {
  const [logoFailed, setLogoFailed] = useState(false)
  return (
    <div className="relative overflow-hidden rounded-3xl text-white shadow-lg px-5 py-5 sm:px-7 sm:py-6" style={{ background: theme.hero }}>
      <span className="pointer-events-none absolute -right-10 -top-16 w-56 h-56 rounded-full bg-white/10" aria-hidden="true" />
      <span className="pointer-events-none absolute right-24 -bottom-24 w-56 h-56 rounded-full bg-white/[0.07]" aria-hidden="true" />
      <span className="pointer-events-none absolute left-1/3 -top-8 w-24 h-24 rounded-full bg-white/[0.06]" aria-hidden="true" />
      <div className="relative flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <span className="w-14 h-14 shrink-0 rounded-2xl bg-white/20 ring-1 ring-white/30 backdrop-blur flex items-center justify-center text-xl font-bold">
            {/* Their logo when it is a picture we can show; their initials otherwise, never ours. */}
            {logo && !logoFailed
              ? <img src={logo} alt="" onError={() => setLogoFailed(true)} className="w-10 h-10 object-contain rounded-lg bg-white p-1" />
              : initials(name)}
          </span>
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold leading-tight tracking-tight">{name}</h1>
            <p className="text-sm text-white/85 mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1">
              {role && (
                <span className="inline-flex items-center gap-1.5">
                  Your role
                  <span className="rounded-full bg-white/20 ring-1 ring-white/30 px-2.5 py-0.5 text-xs font-semibold">{role}</span>
                </span>
              )}
              <span className="text-white/75 text-xs">Hello {who}. Here is what is waiting today.</span>
            </p>
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-2">
          <Link to="/approvals" style={{ color: theme.accentDark }}
                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white hover:bg-white/90 rounded-lg px-3.5 py-2 shadow-xs">
            <CheckCircle2 className="w-3.5 h-3.5" /> Approvals
          </Link>
          <Link to="/teams"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-white border border-white/40 hover:bg-white/15 rounded-lg px-3.5 py-2">
            <Users className="w-3.5 h-3.5" /> Teams
          </Link>
        </div>
      </div>
    </div>
  )
}

export default function CompanyHome() {
  const { user } = useAuth()
  const branding = useBranding()
  const theme = useMemo(() => companyTheme(branding.primaryColour), [branding.primaryColour])
  const [f, setF] = useState(null)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)

  const load = () => api.get('/approvals/front').then(r => setF(r.data)).catch(() => setError('Could not load your front page.'))
  useEffect(() => { load() }, [])
  useFreshOnReturn(load)

  // The partner dashboard's chart, given their weeks: drawn as its "over time" charts are -- an area with the
  // busiest week marked -- and every number also in its Table view.
  const chart = useMemo(() => f && ({
    title: 'Documents filed per week',
    kind: 'line',
    labels: f.weeks.map(w => shortDate(w.week)),
    series: [{ name: 'Documents filed', values: f.weeks.map(w => w.filed) }],
    caption: 'Each point is one week, Monday to Sunday, in the dossiers you can see. The last one is this week.',
  }), [f])

  if (error) return <p className="p-6 text-sm text-red-600">{error}</p>
  if (!f) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>

  const name = user?.organisation?.name || branding.productName
  const logo = branding.logoUrl && branding.logoUrl !== OUR_LOGO ? branding.logoUrl : null
  const cards = [
    [f.waiting, 'Waiting for your approval', '/approvals'],
    [f.sent_back, 'Sent back, waiting for a new version', '/approvals?show=sent_back'],
    f.agree ? [f.agree, 'Waiting for you to agree a move', '/approvals'] : [f.proposed, 'Waiting for the team to agree a move', '/teams'],
    // "6 of 9" is drawn as a ring, as the partner dashboard draws it.
    [f.open ? `${f.ready} of ${f.open}` : 0, 'Open dossiers ready to submit', '/teams'],
  ]

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5" style={themeVars(theme)}>
      <Banner theme={theme} name={name} logo={logo} role={user?.display_role} who={(user?.full_name || '').split(' ')[0]} />

      {notice && (
        <div className={`rounded-lg border px-4 py-2.5 text-sm flex items-center justify-between ${notice.kind === 'error'
          ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-800'}`}>
          {notice.text}
          <button onClick={() => setNotice(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map(([value, label, to]) => (
          <Link key={label} to={to} className="block rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
            <StatCard stat={{ value, label }} colour={theme.accent} />
          </Link>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4 items-start">
        <ViewChart chart={chart} theme={theme} />

        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-xs">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-800">Approvals: waiting for you</h2>
            <Link to="/approvals" className="text-xs font-medium hover:underline flex items-center gap-1" style={{ color: theme.accent }}>
              All <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {f.approve.length === 0
            ? <p className="px-4 py-4 text-sm text-gray-400">Nothing waits for your approval.</p>
            : <ul className="divide-y divide-gray-100">
                {f.approve.map(r => (
                  <li key={r.item_id} className="px-4 py-2.5 flex flex-wrap items-center gap-2">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: theme.accent }} aria-hidden="true" />
                    <div className="min-w-0 mr-auto">
                      <Link to={r.link} className="text-sm text-gray-900 hover:underline">{r.title}</Link>
                      <span className="text-sm text-gray-500"> · {r.dossier_title}</span>
                      <p className="text-xs text-gray-500">
                        {r.folder === 'submission' ? 'Submission folder, final' : 'Working folder'}
                        {r.version > 1 ? ` · version ${r.version}` : ''}{r.since ? ` · ${formatDateTime(r.since)}` : ''}
                      </p>
                    </div>
                    <DecideButtons row={r} onDone={(text) => { setNotice({ kind: 'ok', text }); load() }}
                                   onError={(text) => setNotice({ kind: 'error', text })} />
                  </li>
                ))}
              </ul>}
        </div>
      </div>

      {f.dossiers.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-xs">
          <div className="px-4 py-3 border-b border-gray-100"><h2 className="text-sm font-semibold text-gray-800">Open dossiers</h2></div>
          <ul className="divide-y divide-gray-100">
            {f.dossiers.map(d => (
              <li key={d.id} className="px-4 py-2.5 flex items-center gap-3">
                <FolderOpen className="w-4 h-4 shrink-0" style={{ color: theme.accent }} />
                <Link to={`/teams/${d.team_id}?dossier=${d.id}`} className="mr-auto text-sm text-gray-900 hover:underline">
                  {d.title} <span className="text-xs font-mono text-gray-400">{d.dossier_number}</span>
                </Link>
                <span className="text-xs text-gray-500">{d.counts.in_submission ?? 0} in the Submission folder</span>
                {d.ready && <span className="text-[11px] px-2 py-0.5 rounded-full border bg-green-50 text-green-700 border-green-200">Ready to submit</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
